import styled from 'styled-components'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import routes from '@Constants/Routes'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { AbwaabHeroBanner } from '@Assets/Brand'
import { trackEvent } from '@Utilities/marketingTags'

type CTASectionProps = {
	/**
	 * 'desktop': full-bleed banner band, no CTAs.
	 * 'mobile': the admin's 4:3 phone crop when one is set (falling back to the
	 * desktop artwork) at column width, with the design's blue field + the two
	 * 48px CTAs kept below it.
	 * Both overlay/append the settings-driven copy ONLY when the backend
	 * provides it — the artwork carries its own baked-in headline, so an
	 * empty app_settings headline/subheadline renders nothing (no fallback).
	 */
	variant?: 'desktop' | 'mobile'
}

/**
 * Wrap an image source for CSS `url()`.
 *
 * Quoted, because an unquoted url() token cannot carry spaces, parentheses or
 * unencoded non-ASCII — all of which an uploaded filename can produce, and any
 * one of which makes the declaration invalid. The strip covers the quote and
 * backslash that would otherwise let an admin-supplied string close the quote
 * and append CSS of its own.
 */
const cssUrl = (src: string) => `url("${src.replace(/["\\\r\n]/g, '')}")`

const CTASection: React.FC<CTASectionProps> = ({ variant = 'desktop' }) => {
	const navigate = useNavigate()
	const { t } = useTranslation(['layouts'])
	const { landing_headline, landing_subheadline, assets } = useAppSelector(s => s.settings)

	const headline = landing_headline?.trim()
	const subheadline = landing_subheadline?.trim()
	const hasCopy = Boolean(headline || subheadline)
	/* Admin-supplied artwork wins, the bundled banner is the fallback — the same
	   shape the header/footer use for the logo. Rendered without waiting on
	   assetsLoading: this is a full-bleed, above-the-fold band, and a blank one
	   while /settings/assets resolves is worse than briefly showing the default. */
	const heroSrc = assets?.landing_main || AbwaabHeroBanner
	/* The admin's dedicated 4:3 phone crop (landing_main_mobile). Optional —
	   phones fall back to the full desktop hero in a shorter band. */
	const mobileSrc = assets?.landing_main_mobile

	if (variant === 'mobile') {
		return (
			<MobileHero>
				<BannerImg src={mobileSrc || heroSrc} alt="أبواب — بيئتك التعليمية المتكاملة" />
				<MobileBody>
					{headline && <MobileTitle>{headline}</MobileTitle>}
					{subheadline && <MobileSub>{subheadline}</MobileSub>}
					<MobileActions>
						<YellowBtn
							type="button"
							onClick={() => {
								trackEvent('CTAClick', { cta_text: 'signup', cta_location: 'hero' })
								navigate(routes.signup)
							}}
						>
							{t('header.start_with_abwaab', { ns: 'layouts', defaultValue: 'ابدأ مع أبواب' })}
						</YellowBtn>
						<OutlineBtn
							type="button"
							onClick={() => {
								trackEvent('CTAClick', { cta_text: 'login', cta_location: 'hero' })
								navigate(routes.login)
							}}
						>
							{t('header.have_account', { ns: 'layouts', defaultValue: 'عندي حساب' })}
						</OutlineBtn>
					</MobileActions>
				</MobileBody>
			</MobileHero>
		)
	}

	return (
		<Band $img={heroSrc} $adminArt={Boolean(assets?.landing_main)}>
			{hasCopy && (
				<>
					<Scrim />
					<Inner>
						<HeroContent>
							{headline && <Title>{headline}</Title>}
							{subheadline && <Subtitle>{subheadline}</Subtitle>}
						</HeroContent>
					</Inner>
				</>
			)}
		</Band>
	)
}

export default CTASection

/* ── Mobile hero: the banner artwork, then the design's brand-blue field with
      copy (when provided) and the two CTAs ── */
const MobileHero = styled.section`
	background: var(--blue);
`
const BannerImg = styled.img`
	display: block;
	width: 100%;
	height: auto;
`
const MobileBody = styled.div`
	padding: 20px 16px 24px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	text-align: start;
`
const MobileActions = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 4px;
	/* the two CTAs share the row 50/50 at every width of the mobile variant */
	> button {
		flex: 1;
		height: 48px;
	}
`
const MobileTitle = styled.h1`
	margin: 0;
	font: var(--fw-bold) 24px/1.4 var(--font-sans);
	color: var(--text-on-brand);
`
const MobileSub = styled.p`
	margin: 0;
	font: var(--fw-regular) 14px/1.7 var(--font-sans);
	color: rgba(255, 255, 255, 0.85);
`

/* Full-bleed banner band (edge-to-edge). The band's ratio must match the
   artwork's own, or `cover` shaves the sides: the admin cropper locks 16:5
   (AssetsTab), while the bundled fallback banner is 1920×750 — so the ratio
   follows the source. Settings copy (when present) overlays on the scrim side. */
const Band = styled.section<{ $img: string; $adminArt: boolean }>`
	position: relative;
	overflow: hidden;
	width: 100%;
	/* min-height is the floor for browsers without aspect-ratio support —
	   otherwise an empty band (no settings copy) would collapse to 0. Kept
	   below 16:5's 320px height at the 1024px desktop minimum, so it never
	   re-crops the artwork. */
	min-height: 300px;
	aspect-ratio: ${p => (p.$adminArt ? '16 / 5' : '1920 / 750')};
	/* Longhands, not the background shorthand: one invalid component of a
	   shorthand invalidates the WHOLE declaration, so an admin URL that CSS
	   could not tokenise took the blue down with it and left a white band.
	   Split like this, the colour always survives a bad image URL. */
	background-color: #0c4ed0;
	background-image: ${p => cssUrl(p.$img)};
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	display: flex;
	align-items: center;
`
const Inner = styled.div`
	position: relative;
	width: 100%;
	max-width: 1180px;
	margin: 0 auto;
	padding: 0 16px;
	display: flex;
	/* Keep the content on the dark-scrim side (physical right) in both directions —
	   the artwork's photo sits on the left, so this stays readable in LTR and RTL. */
	justify-content: flex-end;
	html[dir='rtl'] & {
		justify-content: flex-start;
	}
	@media (min-width: 768px) {
		padding: 0 24px;
	}
`
const Scrim = styled.div`
	position: absolute;
	inset: 0;
	pointer-events: none;
	background: linear-gradient(270deg, rgba(2, 26, 72, 0.86) 0%, rgba(2, 26, 72, 0.6) 30%, rgba(2, 26, 72, 0.15) 52%, rgba(2, 26, 72, 0) 68%);
`
const HeroContent = styled.div`
	position: relative;
	width: 100%;
	padding: 36px 0;
	text-align: right;
	@media (min-width: 768px) {
		width: 52%;
		padding: 56px 0;
	}
`
const Title = styled.h1`
	margin: 0;
	font: var(--fw-bold) 26px/1.2 var(--font-sans);
	color: #fff;
	text-shadow: 0 2px 14px rgba(0, 18, 55, 0.3);
	@media (min-width: 768px) {
		font-size: 48px;
		line-height: 1.15;
	}
`
const Subtitle = styled.p`
	margin: 14px 0 0;
	font: var(--fw-regular) 15px/1.6 var(--font-sans);
	color: rgba(255, 255, 255, 0.92);
	max-width: 440px;
	/* Push the block to the physical-right scrim side in both directions (auto left margin). */
	margin-left: auto;
	@media (min-width: 768px) {
		font-size: 18px;
		margin-top: 18px;
	}
`

/* ── Mobile CTA buttons (mobile variant only — the desktop band has none) ── */
const baseBtn = `
	height: 48px;
	padding: 0 24px;
	border-radius: var(--r-md);
	font: var(--fw-bold) 15px var(--font-sans);
	cursor: pointer;
	flex: 1;
`
const YellowBtn = styled.button`
	${baseBtn};
	border: none;
	background: var(--yellow);
	color: var(--navy);
	box-shadow: 0 4px 14px rgba(0, 18, 55, 0.22);
	transition: filter var(--dur-fast) var(--ease);
	&:hover {
		filter: brightness(0.96);
	}
`
const OutlineBtn = styled.button`
	${baseBtn};
	border: 1.5px solid rgba(255, 255, 255, 0.7);
	background: rgba(255, 255, 255, 0.1);
	color: #fff;
	transition: background var(--dur-fast) var(--ease);
	&:hover {
		background: rgba(255, 255, 255, 0.2);
	}
`
